import React, { useEffect, useRef, useState } from 'react';
import { View, Animated, Platform, StyleSheet, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCButton from '@/Atoms/CCButton';

interface RenewalExpiryModalProps {
  days_to_expiry: number | null;
  state: 'normal' | 'warning' | 'urgent' | 'expired';
  onRenew: () => void;
  onClose: () => void;
  onPuzzleChallenge?: () => void;
  couponCode?: string;
  couponDiscount?: number;
  couponExpiresAt?: string; // ISO string
}

// ── Countdown helpers ───────────────────────────────────────────────────────
function getTimeLeft(expiresAt: string): { total: number; days: number; hours: number; minutes: number; seconds: number } {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  const total = diff;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { total, days, hours, minutes, seconds };
}

function formatCountdown(tl: ReturnType<typeof getTimeLeft>): string {
  if (tl.total <= 0) return 'Expired';
  if (tl.days >= 1) {
    return `${tl.days}d ${tl.hours}h ${tl.minutes}m`;
  }
  const hh = String(tl.hours).padStart(2, '0');
  const mm = String(tl.minutes).padStart(2, '0');
  const ss = String(tl.seconds).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

// ── Coupon countdown sub-component ──────────────────────────────────────────
function CouponCountdown({ couponCode, couponDiscount, couponExpiresAt, onRenew }: {
  couponCode: string;
  couponDiscount: number;
  couponExpiresAt: string;
  onRenew: () => void;
}) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(couponExpiresAt));
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const glowAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(couponExpiresAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [couponExpiresAt]);

  // Urgency: < 1 day = urgent, < 3 days = warning
  const isUrgent = timeLeft.total > 0 && timeLeft.days < 1;
  const isWarning = timeLeft.total > 0 && timeLeft.days < 3;

  useEffect(() => {
    const speed = isUrgent ? 600 : isWarning ? 1200 : 2000;
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1, duration: speed, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(pulseAnim, { toValue: 0, duration: speed, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    const glow = Animated.loop(
      Animated.sequence([
        Animated.timing(glowAnim, { toValue: 1, duration: speed * 1.2, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(glowAnim, { toValue: 0, duration: speed * 1.2, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    pulse.start();
    glow.start();
    return () => {
      pulse.stop();
      glow.stop();
    };
  }, [isUrgent, isWarning, pulseAnim, glowAnim]);

  const timerColor = isUrgent ? CCColors.Error : isWarning ? CCColors.Pink : CCColors.Green;
  const borderColor = isUrgent ? CCColors.Error : isWarning ? CCColors.Pink : CCColors.Primary.Default;

  const animatedBorderOpacity = pulseAnim.interpolate({ inputRange: [0, 1], outputRange: [0.5, 1] });
  const animatedScale = glowAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.03] });

  // Web fallback
  const [pulseVal, setPulseVal] = useState(0);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let frame: number;
    const tick = () => {
      // @ts-ignore
      setPulseVal(pulseAnim._value || 0);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [pulseAnim]);

  const webBorderOpacity = 0.5 + pulseVal * 0.5;
  const webScale = 1 + pulseVal * 0.03;

  if (timeLeft.total <= 0) return null;

  return (
    <Animated.View
      style={[
        styles.couponContainer,
        { borderColor },
        Platform.OS === 'web'
          ? { transform: [{ scale: webScale }], opacity: webBorderOpacity * 1.5 > 1 ? 1 : webBorderOpacity * 1.5 }
          : {
            transform: [{ scale: animatedScale }],
            opacity: animatedBorderOpacity,
          },
      ]}>
      <View style={styles.couponHeader}>
        <CCText fontSize={14} fontWeight="bold" color={CCColors.Primary.Brown}>
          🎟 Your Discount Coupon
        </CCText>
        <View style={[styles.discountBadge, { backgroundColor: CCColors.Green }]}>
          <CCText fontSize={12} fontWeight="bold" color={CCColors.White}>
            {couponDiscount}% OFF
          </CCText>
        </View>
      </View>

      <View style={styles.couponCodeRow}>
        <CCText fontSize={20} fontWeight="bold" color={CCColors.Primary.Brown} style={styles.couponCode}>
          {couponCode}
        </CCText>
      </View>

      <View style={[styles.timerRow, { backgroundColor: isUrgent ? '#fff0f0' : isWarning ? '#fff8f0' : '#f0fff4' }]}>
        <CCText fontSize={12} color={timerColor}>
          ⏳ Expires in{' '}
        </CCText>
        <CCText fontSize={14} fontWeight="bold" color={timerColor} style={styles.timerValue}>
          {formatCountdown(timeLeft)}
        </CCText>
        {isUrgent && (
          <CCText fontSize={11} color={CCColors.Error} style={styles.urgentLabel}>
            {' '}· Act now!
          </CCText>
        )}
      </View>

      <CCButton onPress={onRenew} style={styles.couponRenewBtn}>
        <CCText fontSize={14} fontWeight="bold" color={CCColors.Primary.Brown}>
          Use Coupon · Renew Now
        </CCText>
      </CCButton>
    </Animated.View>
  );
}

// ── Animated puzzle CTA button ──────────────────────────────────────────────
function AnimatedPuzzleButton({ onPress }: { onPress: () => void }) {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(scaleAnim, { toValue: 1.05, duration: 850, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(scaleAnim, { toValue: 1, duration: 850, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [scaleAnim]);

  const [scaleVal, setScaleVal] = useState(1);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let frame: number;
    const tick = () => {
      // @ts-ignore
      setScaleVal(scaleAnim._value || 1);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [scaleAnim]);

  return (
    <Animated.View
      style={[
        styles.puzzleButtonWrapper,
        Platform.OS === 'web'
          ? { transform: [{ scale: scaleVal }] }
          : { transform: [{ scale: scaleAnim }] },
      ]}>
      <TouchableOpacity activeOpacity={0.88} onPress={onPress} style={styles.puzzleButton}>
        <View style={styles.puzzleIconWrap}>
          <MaterialCommunityIcons name="gift-open-outline" size={20} color="#C97A00" />
        </View>

        <View style={styles.puzzleTextWrap}>
          <View style={styles.surpriseBadge}>
            <CCText fontSize={10} fontWeight="bold" color={CCColors.White} style={styles.surpriseBadgeText}>
              SURPRISE GIFT
            </CCText>
          </View>

          <CCText fontSize={13} fontWeight="bold" color={'#9A5A00'} style={styles.puzzleButtonText}>
            Solve Puzzle & Unlock Discount Coupon
          </CCText>
        </View>

        <MaterialCommunityIcons name="star-four-points" size={18} color="#F4B942" />
      </TouchableOpacity>
    </Animated.View>
  );
}

// ── Main modal ──────────────────────────────────────────────────────────────
const RenewalExpiryModal: React.FC<RenewalExpiryModalProps> = ({
  days_to_expiry,
  state,
  onRenew,
  onClose,
  onPuzzleChallenge,
  couponCode,
  couponDiscount,
  couponExpiresAt,
}) => {
  const shakeAnimation = useRef(new Animated.Value(0)).current;
  const pulseAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const shake = Animated.loop(
      Animated.sequence([
        Animated.timing(shakeAnimation, { toValue: 1, duration: 100, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(shakeAnimation, { toValue: -1, duration: 100, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(shakeAnimation, { toValue: 0, duration: 100, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnimation, { toValue: 1, duration: 1500, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(pulseAnimation, { toValue: 0, duration: 1500, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    shake.start();
    pulse.start();
    return () => {
      shake.stop();
      pulse.stop();
    };
  }, [shakeAnimation, pulseAnimation]);

  const [shakeVal, setShakeVal] = useState(0);
  const [pulseVal, setPulseVal] = useState(0);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let frame: number;
    const tick = () => {
      // @ts-ignore
      setShakeVal(shakeAnimation._value || 0);
      // @ts-ignore
      setPulseVal(pulseAnimation._value || 0);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [shakeAnimation, pulseAnimation]);

  const calendarRotate = shakeAnimation.interpolate({ inputRange: [-1, 0, 1], outputRange: ['-4deg', '0deg', '4deg'] });
  const dateScale = pulseAnimation.interpolate({ inputRange: [0, 1], outputRange: [1, 1.1] });

  const getCalendarBg = () => {
    switch (state) {
      case 'expired':
      case 'urgent':
        return CCColors.RedBg;
      case 'warning':
        return CCColors.Cancel.Pink;
      default:
        return CCColors.BlueAqua;
    }
  };

  const getCalendarTopColor = () => {
    switch (state) {
      case 'expired':
      case 'urgent':
        return CCColors.Error;
      case 'warning':
        return CCColors.Pink;
      default:
        return CCColors.Green;
    }
  };

  const getMessage = () => {
    if (days_to_expiry === null) return 'Your subscription status';
    if (days_to_expiry <= 0) return 'Your subscription has expired';
    return `Your subscription ends in ${days_to_expiry} days`;
  };

  const hasCoupon = !!(couponCode && couponDiscount && couponExpiresAt);

  return (
    <View style={styles.container}>
      <View style={styles.calendarWrapper}>
        <Animated.View
          style={[
            styles.calendar,
            {
              backgroundColor: getCalendarBg(),
              transform: Platform.OS === 'web'
                ? [{ rotate: `${shakeVal * 4}deg` }]
                : [{ rotate: calendarRotate }],
            },
          ]}>
          <View style={[styles.calendarTop, { backgroundColor: getCalendarTopColor() }]}>
            <View style={styles.calendarRing} />
            <View style={styles.calendarRing} />
          </View>
          <View style={styles.calendarDateContainer}>
            <Animated.View
              style={{
                transform: Platform.OS === 'web'
                  ? [{ scale: 1 + pulseVal * 0.1 }]
                  : [{ scale: dateScale }],
              }}>
              <CCText style={[styles.calendarDate, { color: getCalendarTopColor() }]}>
                {days_to_expiry !== null ? Math.max(0, days_to_expiry) : 0}
              </CCText>
            </Animated.View>
          </View>
        </Animated.View>
      </View>

      <View style={styles.messageContainer}>
        <CCText style={styles.title}>
          {state === 'expired' ? 'Plan Expired' : 'Plan Expiring Soon'}
        </CCText>
        <CCText style={styles.description}>{getMessage()}</CCText>
      </View>

      {hasCoupon && (
        <CouponCountdown
          couponCode={couponCode!}
          couponDiscount={couponDiscount!}
          couponExpiresAt={couponExpiresAt!}
          onRenew={onRenew}
        />
      )}

      {!hasCoupon && onPuzzleChallenge && (
        <AnimatedPuzzleButton onPress={onPuzzleChallenge} />
      )}

      <View style={[styles.buttonContainer, { marginTop: hasCoupon || onPuzzleChallenge ? 10 : 0 }]}>
        {!hasCoupon && (
          <CCButton onPress={onRenew} style={[styles.button, styles.renewButton]}>
            <CCText style={styles.buttonText}>Renew Now</CCText>
          </CCButton>
        )}
        <CCButton onPress={onClose} style={[styles.button, styles.closeButton, hasCoupon && styles.fullWidth]}>
          <CCText color={CCColors.White} style={styles.buttonText}>Close</CCText>
        </CCButton>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    padding: 20,
    width: '100%',
  },
  calendarWrapper: {
    position: 'relative',
    marginBottom: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendar: {
    width: 140,
    height: 140,
    borderRadius: 16,
    shadowColor: CCColors.Black,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 10,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    }),
  },
  calendarTop: {
    height: 36,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  calendarRing: {
    width: 10,
    height: 12,
    backgroundColor: CCColors.White,
    opacity: 0.9,
    borderRadius: 3,
  },
  calendarDateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CCColors.White,
  },
  calendarDate: {
    fontSize: 52,
    fontWeight: 'bold',
  },
  messageContainer: {
    alignItems: 'center',
    marginBottom: 20,
    paddingHorizontal: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: CCColors.TextColor.Dark,
    marginBottom: 6,
    textAlign: 'center',
  },
  description: {
    fontSize: 15,
    color: CCColors.TextColor.Grey,
    textAlign: 'center',
    lineHeight: 22,
  },

  // ── Coupon countdown ─────────────────────────────────────────────────────
  couponContainer: {
    width: '100%',
    borderRadius: 14,
    borderWidth: 2,
    backgroundColor: CCColors.Background,
    marginBottom: 16,
    overflow: 'hidden',
  },
  couponHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 8,
  },
  discountBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  couponCodeRow: {
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 14,
  },
  couponCode: {
    letterSpacing: 3,
    textAlign: 'center',
  },
  timerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    marginHorizontal: 14,
    marginBottom: 12,
    borderRadius: 8,
  },
  timerValue: {
    fontVariant: ['tabular-nums'] as any,
  },
  urgentLabel: {},
  couponRenewBtn: {
    margin: 14,
    marginTop: 0,
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 10,
    paddingVertical: 12,
  },

  // ── Puzzle surprise CTA ──────────────────────────────────────────────────
  puzzleButtonWrapper: {
    alignSelf: 'center',
    marginBottom: 10,
    width: '100%',
  },
  puzzleButton: {
    width: '100%',
    backgroundColor: '#FFF7E8',
    borderWidth: 1.5,
    borderColor: '#F4B942',
    borderRadius: 22,
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: '#F4B942',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
    elevation: 5,
  },
  puzzleIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#FFE7B0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  puzzleTextWrap: {
    flex: 1,
    justifyContent: 'center',
  },
  surpriseBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#FF8A00',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginBottom: 4,
  },
  surpriseBadgeText: {
    letterSpacing: 0.6,
  },
  puzzleButtonText: {
    lineHeight: 18,
  },

  // ── Buttons ──────────────────────────────────────────────────────────────
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  button: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  fullWidth: {
    flex: undefined,
    width: '100%',
  },
  renewButton: {
    shadowColor: CCColors.Blue,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  closeButton: {
    backgroundColor: CCColors.Grey.Grey,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default RenewalExpiryModal;